import { SpecialInsert, SpecialRow, getSpecialByID } from "./specials";
import { checkImages, checkVideos } from "./utils";

import { Product } from "./products";
import { VALIDATE_SLUG_REGEX } from "./comedians-utils";
import createNoAuthSupabaseClient from "../supabase/supabase";
import { formatPriceFromCents } from "@punchup/utils/formatPriceFromCents";

export const getSpecialSlugAlreadyInUse = async (
  slug: string,
  comedian_id: string
) => {
  const supabase = createNoAuthSupabaseClient();
  const { count, status, error } = await supabase
    .from("specials")
    .select("slug", { count: "exact", head: true })
    .match({
      slug,
      comedian_id,
    });

  if (error) {
    console.error(error);
  } else if (status === 200) {
    if (count && count > 0) {
      return true;
    } else {
      return false;
    }
  }
  throw new Error(`Error while looking up the slug: ${slug} in the database.`);
};

export const validateSpecialSlug = async (
  slug: string,
  comedian_id: string
) => {
  const regex = new RegExp(VALIDATE_SLUG_REGEX); // we can re-use the same regex used for comedians
  if (!regex.test(slug)) {
    const message = `The slug ${slug} is invalid. Please try a different one.`;
    console.error(message);
    return { error: { message } };
  }

  // Verify slug is not already used
  const slugAlreadyInUse = await getSpecialSlugAlreadyInUse(slug, comedian_id);
  if (slugAlreadyInUse) {
    const message = `The slug ${slug} is already in use, please try a different one.`;
    console.error(message);
    return { error: { message } };
  }
};

export const validateSpecial = async (special: SpecialInsert) => {
  const {
    id,
    comedian_id,
    title,
    slug,
    runtime,
    price,
    small_preview_img,
    preview_img,
    video_id,
    preview_video_id,
    poster_img_with_titles,
    description,
    is_live,
    published_at,
  } = special;

  if (!slug) {
    throw new Error(
      "You did not provide a slug for this special, please try again."
    );
  }

  // Look up Special with the given id to determine
  // if it exists already or if it's a new one
  let existingSpecial;
  // Note: we unfortunately have to use the try catch for the same reason we had to when creating new comedians
  // getComedian/getSpecialByID throw when they don't find anything instead of just returning null
  try {
    existingSpecial = await getSpecialByID(id!);
  } catch {
    existingSpecial = null;
  }

  // Special did not exist (i.e it's a new one), continue with validation
  if (!existingSpecial) {
    const errors = await validateSpecialSlug(slug, comedian_id);
    if (errors) {
      return errors;
    }
  } else {
    // Skip validation for the slug if it hasn't changed
    if (existingSpecial.slug !== slug) {
      const errors = await validateSpecialSlug(slug, comedian_id);
      if (errors) {
        return errors;
      }
    }
  }

  // if published date exists, checks that all fields are present (videoID, previewImg, smallPreviewImg, etc) else return error
  const images = [small_preview_img, preview_img];
  const videos = [video_id];

  if (published_at) {
    const inputs = [...images, ...videos];
    const invalid = (item: string | null | undefined) =>
      item === null || item === undefined || item === "";
    if (inputs.some(invalid)) {
      const message =
        "You requested to publish a special that is missing one or more of Small Preview Img, Preview Img, or Video ID. Please add those before trying again.";
      console.error(message);
      return {
        error: message,
      };
    }
  }

  // validate that cloudflare resources are valid
  const imgError = await checkImages(images);
  if (imgError) {
    return imgError;
  }
  const videoError = await checkVideos(videos);
  if (videoError) {
    return videoError;
  }
};

// Shared shape for any content row (special, collection, podcast, etc.) that
// participates in the subscription-tier access model.
export type SubscriptionAccessFields = {
  requires_subscription: boolean;
  required_subscription_tier: number | null;
};

// Intended states:
//   (requires_subscription=false, tier=null)   → not gated
//   (requires_subscription=true,  tier>=1)     → gated at that tier
//   (requires_subscription=true,  tier=null)   → legacy pre-tier content; treated as tier 1
// `tier=0` and `(requires_subscription=false, tier>=1)` are not produced by any
// write path and should be considered undefined; the `> 0` check here just keeps
// stray data from routing free content into /subscribe?from_tier=0.
export const isSubscriptionContent = (
  content: SubscriptionAccessFields
): boolean =>
  content.requires_subscription ||
  (content.required_subscription_tier ?? 0) > 0;

// Returns 0 when the content is not subscription-gated.
export const getRequiredSubscriptionTier = (
  content: SubscriptionAccessFields
): number =>
  content.required_subscription_tier ?? (content.requires_subscription ? 1 : 0);

// Centralize function to determine whether a user can access a piece of content
// Since it's something that's used in multiple places
export const getUserCanAccessContent = ({
  special,
  userFollowsComedian,
  userPurchasedSpecial,
  isUserSubscribed,
  userManagesComedian,
  userHasPurchasedBundle,
  userMaxSubscriptionTier,
}: {
  special: SpecialRow;
  userFollowsComedian: boolean;
  userPurchasedSpecial: boolean;
  isUserSubscribed: boolean;
  userManagesComedian?: boolean;
  userHasPurchasedBundle?: boolean;
  userMaxSubscriptionTier?: number;
}): boolean => {
  if (userManagesComedian || userHasPurchasedBundle) {
    return true;
  }
  let userCanAccessSpecial = false;
  if (isSubscriptionContent(special)) {
    // subscription content
    if (userMaxSubscriptionTier !== undefined) {
      userCanAccessSpecial =
        userMaxSubscriptionTier >= getRequiredSubscriptionTier(special);
    } else if (isUserSubscribed) {
      userCanAccessSpecial = true;
    }
  } else {
    // non-subscription content
    userCanAccessSpecial =
      (special.requires_purchase &&
        userFollowsComedian &&
        userPurchasedSpecial) ||
      (!special.requires_purchase && userFollowsComedian) ||
      // this last one is a bit hacky but it's essentially the override to handle the rare edge case
      // of a user that does not follow a comedian but has purchased some of their content.
      userPurchasedSpecial;
  }
  return userCanAccessSpecial;
};

const DEFAULT_PRICING_PRIORITY: Product["category"][] = [
  "subscription", // if there's a subscription product, display  that price
  "video", // otherwise, if there's a video product, display that price
  "collection", // finally, if there's no video or subscription, show the collection price
];

interface GetWatchCTAButtonTextParams {
  special: {
    requires_purchase: boolean;
    requires_subscription: boolean;
    is_live?: boolean | null;
    unlocked?: boolean | null;
  };
  products: Product[];
  pricingPriority?: Product["category"][];
}

const DEFAULT_CTA = "Unlock for Free";

export function getWatchCTAButtonText({
  special,
  products,
  pricingPriority = DEFAULT_PRICING_PRIORITY,
}: GetWatchCTAButtonTextParams) {
  // "loading" state
  if (special.unlocked === null) {
    return DEFAULT_CTA;
  }

  if (special.unlocked) {
    return "Watch Now";
  } else {
    // if not "unlocked", the user must not have access (i.e. unlocked = false)
    // firstly, if the special requires a subscription, show "Upgrade"
    if (special.requires_subscription) {
      return "Upgrade to Watch";
    }

    // evaluate products in the order specified by pricingPriority
    for (const category of pricingPriority) {
      const product = products.find((product) => product.category === category);
      if (!product) continue;

      // upgrade CTA for all subscription products
      if (category === "subscription") {
        return "Upgrade to Watch";
      }

      if (product.is_flex_price) {
        return "Pay What You Want";
      }

      // if the product has a positive price, display it
      if (product.price > 0) {
        const price = formatPriceFromCents(product.price);
        if (category === "collection") {
          return `Unlock Collection for ${price}`;
        }
        return `Unlock for ${price}`;
      }

      // otherwise, must price = 0 (free)
      return "Unlock for Free";
    }

    // no matching products found — special is free and user is not subscribed to the comedian
    return DEFAULT_CTA;
  }
}
